// @ts-nocheck
import React, { useState, useEffect } from "react";
import { callClaude } from "@/legacy/helpersModule";
import { Spin } from "@/shared/components/ui/Spin";
import {
  fetchAllConsentedWithCv,
  bulkUpdateCandidateStatus,
} from "@/shared/api/candidatesApi";
import { Button, Input, useToast } from "@/shared/components/ui";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";

export function Screening({ jobs, onShortlist, onBack }) {
  const toast = useToast();
  useEscapeKey(onBack);
  const [phase, setPhase] = useState("chat");
  const [criteria, setCriteria] = useState("");
  const [scored, setScored] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loadingPool, setLoadingPool] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchAllConsentedWithCv();
        if (!cancelled) setCandidates(list);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingPool(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const consented = candidates.filter((c) => c.consent);

  useEffect(() => {
    if (loadingPool) return;
    (async () => {
      setBusy(true);
      const r = await callClaude(
        [{ role: "user", content: `${consented.length} candidates to screen.` }],
        "You are AIRA HR. Be concise.",
      );
      setMsgs([{ role: "ai", text: r }]);
      setHist([
        { role: "user", content: "Hi." },
        { role: "assistant", content: r },
      ]);
      setBusy(false);
    })();
  }, [loadingPool, consented.length]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const u = input.trim();
    setInput("");
    const h = [...hist, { role: "user", content: u }];
    setMsgs((p) => [...p, { role: "user", text: u }]);
    setBusy(true);
    const r = await callClaude(h, "Be concise.");
    setMsgs((p) => [...p, { role: "ai", text: r }]);
    setHist([...h, { role: "assistant", content: r }]);
    setCriteria((p) => p + " " + u);
    setBusy(false);
  };

  const analyze = async () => {
    setPhase("analyzing");
    const results = [];
    for (const c of consented.filter((c) => c.jobId && c.cv)) {
      const job = jobs.find((j) => j.id === c.jobId) || {};
      const r = await callClaude(
        [
          {
            role: "user",
            content: `Score CV for "${job.title}". Req: ${job.requirements}. Extra: ${criteria}. CV: ${c.cv}. JSON: {"overallScore":0-100,"summary":"...","recommendation":"shortlist|reject"}`,
          },
        ],
        "Return only JSON.",
        true,
      );
      results.push({
        ...c,
        sr: r || {
          overallScore: 50,
          summary: "—",
          recommendation: "shortlist",
        },
      });
    }
    const s = results.sort(
      (a, b) => (b.sr?.overallScore || 0) - (a.sr?.overallScore || 0),
    );
    setScored(s);
    setSel(
      new Set(
        s.filter((c) => c.sr?.recommendation === "shortlist").map((c) => c.id),
      ),
    );
    setPhase("results");
  };

  const confirmShortlist = async () => {
    const updates = [...sel].map((id) => ({ id, status: "SHORTLISTED" }));
    if (updates.length) {
      try {
        await bulkUpdateCandidateStatus(updates);
      } catch (e) {
        console.error(e);
        toast.show({ tone: "danger", title: "Save failed", message: "Could not save shortlist status. Please try again." });
        return;
      }
    }
    onShortlist();
  };

  if (loadingPool) {
    return (
      <div className="min-h-dvh safe-bottom bg-surface-alt flex items-center justify-center">
        <Spin label="Loading candidates…" />
      </div>
    );
  }

  if (phase === "analyzing")
    return (
      <div className="min-h-dvh safe-bottom bg-surface-alt flex items-center justify-center">
        <Spin label="Analysing…" />
      </div>
    );

  if (phase === "results")
    return (
      <div className="min-h-dvh safe-bottom bg-surface-alt">
        <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-bold">Results</span>
          <Button size="sm" className="w-full sm:w-auto" onClick={confirmShortlist}>
            Confirm ({sel.size})
          </Button>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x space-y-3">
          {scored.map((c) => {
            const on = sel.has(c.id);
            return (
              <div
                key={c.id}
                onClick={() =>
                  setSel((p) => {
                    const n = new Set(p);
                    n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                    return n;
                  })
                }
                className={`bg-white rounded-2xl border p-4 cursor-pointer ${on ? "border-brand-300" : "border-line opacity-60"}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold">{c.name}</p>
                    <p className="text-xs text-ink-400">{c.email}</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-black text-brand-600">
                    {c.sr?.overallScore}
                  </p>
                </div>
                <p className="text-sm text-ink-600 mt-2">{c.sr?.summary}</p>
              </div>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt flex flex-col">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-ink-400 hover:text-ink-900"
          >
            ← Back
          </button>
          <span className="font-bold">Screening</span>
        </div>
        <Button
          size="sm"
          className="w-full sm:w-auto"
          onClick={analyze}
          disabled={
            consented.filter((c) => c.cv && c.jobId).length === 0 || busy
          }
        >
          Analyse →
        </Button>
      </div>
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-4 sm:py-6 safe-x flex flex-col">
        <div className="flex-1 bg-white rounded-2xl border border-line flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-3">
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "ai" ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-xs px-4 py-3 rounded-2xl text-sm ${m.role === "ai" ? "bg-slate-100 text-ink-900" : "bg-brand-600 text-white"}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <p className="text-ink-400 text-sm animate-pulse">Typing…</p>
            )}
          </div>
          <div className="border-t border-line p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Criteria…"
              className="flex-1 w-full"
            />
            <Button
              onClick={send}
              disabled={!input.trim() || busy}
              className="w-full sm:w-auto shrink-0"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
